import { useEffect, useState } from "react";
import { Search, Loader2, X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { storefrontApiRequest, STOREFRONT_QUERY, type ShopifyProduct } from "@/lib/shopify";
import { teamHats, teamSlug, type Hat } from "@/data/teamHats";
import { useCurrencyStore, formatConvertedPrice } from "@/stores/currencyStore";

interface TeamHatResult {
  team: string;
  league: string;
  logo: string;
  hat: Hat;
}

const searchTeamHats = (q: string): TeamHatResult[] => {
  const needle = q.toLowerCase().trim();
  if (!needle) return [];
  const out: TeamHatResult[] = [];
  for (const league of Object.keys(teamHats)) {
    for (const entry of teamHats[league]) {
      const matchTeam = entry.team.toLowerCase().includes(needle) || league.toLowerCase().includes(needle);
      for (const hat of entry.hats) {
        const matchHat =
          hat.title.toLowerCase().includes(needle) ||
          hat.style.toLowerCase().includes(needle) ||
          hat.retailer.toLowerCase().includes(needle);
        if (matchTeam || matchHat) {
          out.push({ team: entry.team, league, logo: entry.logo, hat });
          if (out.length >= 12) return out;
        }
      }
    }
  }
  return out;
};

export const SearchDialog = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState<ShopifyProduct[]>([]);
  const [teamResults, setTeamResults] = useState<TeamHatResult[]>([]);
  const [loading, setLoading] = useState(false);
  const displayCurrency = useCurrencyStore((s) => s.code);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open || !q.trim()) {
      setResults([]);
      setTeamResults([]);
      return;
    }
    setTeamResults(searchTeamHats(q));
    const t = setTimeout(() => {
      setLoading(true);
      const safe = q.trim().replace(/[^\w\s-]/g, "");
      if (!safe) { setLoading(false); return; }
      storefrontApiRequest(STOREFRONT_QUERY, { first: 8, query: `title:*${safe}* OR tag:${safe} OR vendor:${safe}` })
        .then((d) => setResults(d?.data?.products?.edges || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  return (
    <>
      <span onClick={() => setOpen(true)} className="inline-flex items-center">{children}</span>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg flex flex-col h-full p-0 gap-0">
          <SheetHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-border">
            <SheetTitle className="font-display text-2xl tracking-wide">SEARCH</SheetTitle>
            <SheetDescription className="text-xs text-muted-foreground">
              Find hats, teams & drops across the Brim Mob universe.
            </SheetDescription>
            <div className="mt-4 group flex items-center gap-3 border border-border bg-secondary/40 focus-within:bg-background focus-within:border-accent transition-colors px-3 py-2.5 rounded-sm">
              <Search className="w-4 h-4 text-muted-foreground group-focus-within:text-accent transition-colors" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search hats, teams, drops…"
                className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground/70"
              />
              {q && (
                <button
                  type="button"
                  onClick={() => setQ("")}
                  aria-label="Clear search"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              <kbd className="text-[10px] text-muted-foreground hidden sm:inline border border-border px-1.5 py-0.5 rounded">ESC</kbd>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
            {!q && (
              <div className="py-12 text-center">
                <Search className="w-10 h-10 text-muted-foreground mx-auto mb-3" aria-hidden="true" />
                <p className="font-display text-sm tracking-widest text-muted-foreground">START TYPING</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Try "Yankees", "fitted", or "drop"</p>
              </div>
            )}

            {loading && (
              <div className="py-12 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-accent" />
              </div>
            )}

            {!loading && q && results.length === 0 && teamResults.length === 0 && (
              <div className="py-12 text-center">
                <p className="font-display text-sm tracking-widest">NO RESULTS</p>
                <p className="text-xs text-muted-foreground mt-2">Nothing matches "{q}".</p>
              </div>
            )}

            {teamResults.length > 0 && (
              <div className="mb-6">
                <p className="font-display text-[10px] tracking-[0.2em] text-muted-foreground mb-3">TEAM HATS</p>
                <ul className="space-y-2">
                  {teamResults.map((r) => (
                    <li key={`${r.team}-${r.hat.id}`}>
                      <Link
                        to={`/team/${teamSlug(r.team)}`}
                        onClick={() => setOpen(false)}
                        className="group flex items-center gap-4 p-2 -mx-2 hover:bg-secondary/60 transition-colors rounded-sm"
                      >
                        <div className="w-14 h-14 bg-muted flex items-center justify-center p-2 flex-shrink-0 rounded-sm">
                          <img src={r.logo} alt={r.team} className="max-w-full max-h-full object-contain" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-display text-sm truncate group-hover:text-accent transition-colors">{r.hat.title}</p>
                          <p className="text-xs text-muted-foreground truncate">{r.league} • {r.hat.style} • <span className="text-accent">{r.hat.price}</span></p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!loading && results.length > 0 && (
              <div>
                <p className="font-display text-[10px] tracking-[0.2em] text-muted-foreground mb-3">PRODUCTS</p>
                <ul className="space-y-2">
                  {results.map((p) => {
                    const img = p.node.images.edges[0]?.node;
                    const price = p.node.priceRange.minVariantPrice;
                    return (
                      <li key={p.node.id}>
                        <Link
                          to={`/product/${p.node.handle}`}
                          onClick={() => setOpen(false)}
                          className="group flex items-center gap-4 p-2 -mx-2 hover:bg-secondary/60 transition-colors rounded-sm"
                        >
                          <div className="w-14 h-14 bg-muted overflow-hidden flex-shrink-0 rounded-sm">
                            {img && <img src={img.url} alt={img.altText || p.node.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" decoding="async" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-display text-sm truncate group-hover:text-accent transition-colors">{p.node.title}</p>
                            <p className="text-xs text-muted-foreground">{formatConvertedPrice(price.amount, price.currencyCode, displayCurrency)}</p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};